import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import axios from "axios";
import { ShieldCheck, ShieldAlert, Loader2, Download, RefreshCw, CheckCircle2, Circle, Mail } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => (Number(n) ? "$" + Number(n).toLocaleString("en-US", { maximumFractionDigits: 2 }) : "—");

export default function CasePortal() {
  const { caseId } = useParams();
  const t = new URLSearchParams(useLocation().search).get("t") || "";
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => {
    try {
      const r = await axios.get(`${API}/financial-crimes/case-view/${caseId}?t=${encodeURIComponent(t)}`);
      setC(r.data);
    } catch (e) {
      setC({ found: false, error: e.response?.data?.detail || "Could not load case." });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-line */ }, []);

  const toggle = async (key, done) => {
    try {
      const r = await axios.post(`${API}/financial-crimes/case-checklist/${caseId}?t=${encodeURIComponent(t)}`, { key, done });
      setC(r.data);
    } catch { /* noop */ }
  };
  const resend = async () => {
    setResending(true); setMsg("");
    try {
      const r = await axios.post(`${API}/financial-crimes/case-resend/${caseId}?t=${encodeURIComponent(t)}`);
      setC(r.data); setMsg("Your case kit was re-sent to your email" + (r.data.fax ? " and fax." : "."));
    } catch (e) { setMsg(e.response?.data?.detail || "Could not re-send."); }
    finally { setResending(false); }
  };

  const card = { background: "#0A241A", border: "1px solid #14503B", borderRadius: 14, padding: 20, marginBottom: 16 };
  const head = { fontSize: 11, fontWeight: 800, color: "#6FD3AE", textTransform: "uppercase", letterSpacing: ".12em", marginBottom: 12 };

  return (
    <div style={{ background: "#06160F", minHeight: "100vh", color: "#E7F3EE", fontFamily: "'Manrope',system-ui,sans-serif" }} data-testid="fciu-case-portal">
      <div style={{ maxWidth: 680, margin: "0 auto", padding: "40px 20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
          <img src="/seutrade_logo.png" alt="SEUTRADE" style={{ width: 54, height: 54, borderRadius: "50%", background: "#fff", padding: 3 }} />
          <div>
            <div style={{ color: "#6FD3AE", fontWeight: 800, letterSpacing: ".08em", fontSize: 12, textTransform: "uppercase" }}>Financial Crimes Investigations Unit</div>
            <div style={{ color: "#9ec9ba", fontSize: 12 }}>Your Case Portal</div>
          </div>
        </div>

        {loading && <div style={{ display: "flex", gap: 10, color: "#9ec9ba", padding: "40px 0" }} data-testid="cp-loading"><Loader2 className="animate-spin" size={20} /> Loading your case…</div>}

        {!loading && c && !c.found && (
          <div style={{ ...card, borderColor: "#6b2d22", background: "#2a1512" }} data-testid="cp-error">
            <div style={{ display: "flex", gap: 10, color: "#ff9b86", fontWeight: 800 }}><ShieldAlert size={20} /> Case unavailable</div>
            <p style={{ color: "#e7b9ae", fontSize: 13, marginTop: 8 }}>{c.error || "This case link is invalid or expired. Please use the link from your confirmation email."}</p>
          </div>
        )}

        {!loading && c && c.found && (
          <div data-testid="cp-result">
            <div style={card}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                {c.delivered ? <ShieldCheck size={22} color="#6FD3AE" /> : <Loader2 size={20} className="animate-spin" color="#E4C97A" />}
                <div style={{ fontSize: 17, fontWeight: 800, color: c.delivered ? "#9EEBCB" : "#E4C97A" }}>
                  {c.delivered ? "Your case kit is ready" : c.paid ? "Assembling your kit…" : "Awaiting payment"}
                </div>
              </div>
              <div style={{ fontSize: 13, color: "#C8DAD2", lineHeight: 1.7 }}>
                <div><b>Complainant:</b> {c.victim_name || "—"}</div>
                <div><b>Reported loss:</b> {money(c.loss_amount)}</div>
                {c.case_ref && <div><b>Case reference:</b> {c.case_ref}</div>}
                {c.delivered && <div><b>Delivered:</b> {c.emailed?.length ? `emailed to ${c.emailed.join(", ")}` : ""}{c.fax ? ` · faxed (${c.fax.status})` : ""}</div>}
              </div>
              {c.paid && (
                <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
                  <a href={`${API}/financial-crimes/case-kit/${caseId}?t=${encodeURIComponent(t)}`} target="_blank" rel="noopener noreferrer" data-testid="cp-download"
                    style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#6FD3AE", color: "#06160F", textDecoration: "none", padding: "11px 18px", borderRadius: 10, fontSize: 13, fontWeight: 800 }}>
                    <Download size={16} /> Download case kit
                  </a>
                  <button onClick={resend} disabled={resending} data-testid="cp-resend"
                    style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "transparent", border: "1px solid #14503B", color: "#6FD3AE", padding: "11px 18px", borderRadius: 10, fontSize: 13, fontWeight: 800, cursor: "pointer" }}>
                    {resending ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />} Re-send to me
                  </button>
                </div>
              )}
              {msg && <div style={{ color: "#6FD3AE", fontSize: 12.5, marginTop: 10 }} data-testid="cp-msg">{msg}</div>}
            </div>

            <div style={card}>
              <div style={head}>Recovery checklist</div>
              {c.checklist_items.map((it) => {
                const done = !!c.checklist[it.key];
                return (
                  <button key={it.key} onClick={() => toggle(it.key, !done)} data-testid={`cp-check-${it.key}`}
                    style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", background: "transparent", border: "none", color: done ? "#9EEBCB" : "#C8DAD2", padding: "8px 0", fontSize: 13.5, cursor: "pointer" }}>
                    {done ? <CheckCircle2 size={18} color="#6FD3AE" /> : <Circle size={18} color="#3f5a4e" />}
                    <span style={{ textDecoration: done ? "line-through" : "none", opacity: done ? 0.8 : 1 }}>{it.label}</span>
                  </button>
                );
              })}
            </div>

            <div style={{ ...card, background: "#0A1813", borderColor: "#123528", fontSize: 12, color: "#9ec9ba", lineHeight: 1.6 }} data-testid="cp-disclaimer">
              <Mail size={13} style={{ verticalAlign: "-2px" }} /> Keep your confirmation email for this secure link. SEUTRADE is not a law firm and does not provide legal advice, surveillance, or people-location services. Submit the prepared complaints to the agencies listed in your kit and consult a licensed attorney for civil recovery.
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
